/**
 * EmailApprovalDialog — Iter88zi (2026-02-19)
 *
 * Manuelle Freigabe vor jedem Kunden-Mail-Versand. Zeigt Empfänger,
 * editierbare CC-Liste (mit Autofill aus dem Rep-Verlauf), Betreff,
 * Anhang-Liste + kurze Vorschau. Absender kann Adressen ergänzen und
 * erst nach explizitem Klick auf „Jetzt senden" wird die eigentliche
 * Sende-Aktion (übergeben als `onConfirm`) ausgelöst.
 *
 * Nutzung:
 *   <EmailApprovalDialog
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onConfirm={async (cc) => { await api.post(..., { ..., cc }); }}
 *     recipient="max@example.com"
 *     subject="1. Anzahlungsrechnung · WP26001946-AZ1 · NOVATUS Energie"
 *     attachments={[{ name: "Rechnung_WP26001946-AZ1.pdf", size: 42000 }]}
 *     bodyPreview="Sehr geehrter Herr Bartsch, ..."
 *   />
 */
import { useEffect, useState } from "react";
import { X, Send, Paperclip, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { TeamCopyNote } from "@/components/TeamCopyNote";

const AUTOFILL_KEY = "cc_email";

export default function EmailApprovalDialog({
  open, onClose, onConfirm,
  recipient, subject, attachments = [], bodyPreview = "",
  defaultCc = [], testIdPrefix = "email-approval",
  // Iter88zo (2026-02-19) — optionale PDF-Vorschau. Der Aufrufer gibt
  // einen `pdfFetcher`-Callback (async () => Blob) mit; wir wandeln
  // ihn beim Öffnen in eine Object-URL um und rendern sie als <iframe>.
  pdfFetcher = null,
  projectId = null,
}) {
  const [cc, setCc] = useState(defaultCc);
  const [newCc, setNewCc] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [sending, setSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCc(defaultCc || []);
    setNewCc("");
    // Autofill aus Rep-Historie laden
    api.get(`/autofill/${AUTOFILL_KEY}`).then((r) => {
      setSuggestions((r.data?.values || []).filter((v) => !(defaultCc || []).includes(v)));
    }).catch(() => setSuggestions([]));

    // PDF-Vorschau laden
    if (pdfFetcher) {
      setPdfLoading(true);
      setPdfError(null);
      pdfFetcher()
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        })
        .catch((e) => {
          setPdfError(e?.response?.data?.detail || e?.message || "PDF-Vorschau nicht verfügbar");
          setPdfUrl(null);
        })
        .finally(() => setPdfLoading(false));
    } else {
      setPdfUrl(null);
    }
    return () => {
      // Cleanup blob URL beim Schließen
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [open]);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  // Iter88zs (2026-02-19) — Bulk-Add: unterstützt Semikolon, Komma,
  // Leerzeichen und Zeilenumbrüche als Trennzeichen. Beispiel:
  //   "a@b.de; c@d.de , e@f.de\ng@h.de" → 4 CCs
  // Damit können Reps Outlook/Excel-Listen 1:1 einfügen.
  const addCc = (addr) => {
    const raw = (addr || "").trim();
    if (!raw) return;
    const tokens = raw.split(/[\s,;]+/).map((t) => t.trim().toLowerCase()).filter(Boolean);
    const added = [];
    const skipped = [];
    const invalid = [];
    for (const v of tokens) {
      if (!isValidEmail(v)) { invalid.push(v); continue; }
      if (cc.includes(v) || added.includes(v) || v === recipient?.toLowerCase()) { skipped.push(v); continue; }
      added.push(v);
    }
    if (added.length) {
      setCc([...cc, ...added]);
      setSuggestions((s) => s.filter((x) => !added.includes(x)));
      if (tokens.length > 1) {
        toast.success(`${added.length} Adresse${added.length === 1 ? "" : "n"} übernommen${skipped.length ? ` (${skipped.length} Duplikate übersprungen)` : ""}`);
      }
    }
    if (invalid.length) {
      toast.error(`${invalid.length} ungültige Adresse${invalid.length === 1 ? "" : "n"}: ${invalid.slice(0, 3).join(", ")}${invalid.length > 3 ? "…" : ""}`);
    } else if (!added.length && skipped.length) {
      toast.warning("Adresse(n) bereits enthalten");
    }
    setNewCc("");
  };

  const removeCc = (addr) => setCc(cc.filter((a) => a !== addr));

  const confirm = async () => {
    // Iter88zs — Sicherheitsnetz: wenn im CC-Input noch ungeliste Adressen
    // stehen, fügen wir sie automatisch hinzu, bevor gesendet wird.
    // Verhindert die Situation „ich hab CCs getippt, aber der Versand ging
    // ohne raus, weil ich CC-Button vergessen habe".
    let finalCc = cc;
    const pending = (newCc || "").trim();
    if (pending) {
      const tokens = pending.split(/[\s,;]+/).map((t) => t.trim().toLowerCase()).filter(Boolean);
      const valid = tokens.filter((v) => isValidEmail(v) && !finalCc.includes(v) && v !== recipient?.toLowerCase());
      if (valid.length) {
        finalCc = [...finalCc, ...valid];
        setCc(finalCc);
        setNewCc("");
        toast.info(`${valid.length} Adresse${valid.length === 1 ? "" : "n"} aus Eingabefeld nachgereicht`);
      }
    }

    setSending(true);
    try {
      await onConfirm(finalCc);
      // Erfolgreichen CCs im Autofill-Verlauf speichern
      for (const c of finalCc) {
        try { await api.post(`/autofill/${AUTOFILL_KEY}`, { value: c }); } catch (_e) { /* autofill best-effort */ }
      }
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" data-testid={`${testIdPrefix}-overlay`}>
      <div className="w-full max-w-4xl bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Send size={18} className="text-emerald-400" />
            <h2 className="font-display font-bold text-lg text-zinc-100">E-Mail-Versand freigeben</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-800 rounded-lg" data-testid={`${testIdPrefix}-close`}>
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1 text-[13px]">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Empfänger</div>
            <div className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg font-mono text-zinc-200" data-testid={`${testIdPrefix}-recipient`}>
              {recipient || "—"}
            </div>
          </div>
          <TeamCopyNote projectId={projectId} />

          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center justify-between">
              <span>CC (optional — Kopie auch an…)</span>
              <span className="text-zinc-600">{cc.length} Adressen</span>
            </div>
            {cc.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {cc.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 bg-emerald-900/30 border border-emerald-500/40 text-emerald-200 rounded-md text-[12px] font-mono"
                        data-testid={`${testIdPrefix}-cc-chip-${c}`}>
                    {c}
                    <button onClick={() => removeCc(c)} className="p-0.5 hover:bg-emerald-800/50 rounded"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input value={newCc}
                     onChange={(e) => setNewCc(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCc(newCc); } }}
                     onBlur={() => { if (newCc.trim() && /[;,\s]/.test(newCc.trim())) addCc(newCc); }}
                     onPaste={(e) => {
                       // Iter88zs — direktes Verarbeiten von eingefügten
                       // Multi-Adressen (Outlook, Excel-Zellen etc.).
                       const pasted = (e.clipboardData || window.clipboardData).getData("text");
                       if (pasted && /[;,\n]/.test(pasted)) {
                         e.preventDefault();
                         addCc(pasted);
                       }
                     }}
                     placeholder="name@firma.de (mehrere: mit ; oder , trennen)"
                     className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-emerald-500/50 text-[13px]"
                     data-testid={`${testIdPrefix}-cc-input`} />
              <button onClick={() => addCc(newCc)}
                      className="px-3 py-2 bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 rounded-lg hover:bg-emerald-600/30 flex items-center gap-1.5 text-[12px] font-bold"
                      data-testid={`${testIdPrefix}-cc-add`}>
                <UserPlus size={14} /> CC
              </button>
            </div>
            <div className="text-[10.5px] text-zinc-500 mt-1">
              Mehrere Adressen mit <code className="text-emerald-300">;</code> oder <code className="text-emerald-300">,</code> trennen — auch Copy-Paste aus Outlook oder Excel funktioniert.
            </div>
            {suggestions.length > 0 && (
              <div className="mt-2">
                <div className="text-[10.5px] text-zinc-500 mb-1">Vorschläge aus deinem CC-Verlauf:</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.slice(0, 6).map((s) => (
                    <button key={s} onClick={() => addCc(s)}
                            className="px-2 py-0.5 rounded-md text-[11px] font-mono text-zinc-300 bg-zinc-900 border border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-200"
                            data-testid={`${testIdPrefix}-cc-suggest-${s}`}>+ {s}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Betreff</div>
            <div className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-200" data-testid={`${testIdPrefix}-subject`}>
              {subject || "—"}
            </div>
          </div>

          {attachments.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Anhänge ({attachments.length})</div>
              <ul className="space-y-1">
                {attachments.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg" data-testid={`${testIdPrefix}-attach-${i}`}>
                    <Paperclip size={14} className="text-zinc-500" />
                    <span className="font-mono text-zinc-300 text-[12px]">{a.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(pdfFetcher || pdfUrl || pdfLoading || pdfError) && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center justify-between">
                <span>PDF-Vorschau</span>
                {pdfUrl && (
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                     className="text-emerald-400 hover:text-emerald-300 text-[11px] font-mono"
                     data-testid={`${testIdPrefix}-pdf-open`}>
                    In neuem Tab öffnen ↗
                  </a>
                )}
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                {pdfLoading && (
                  <div className="flex items-center justify-center gap-2 py-16 text-zinc-500 text-[13px]" data-testid={`${testIdPrefix}-pdf-loading`}>
                    <Loader2 size={16} className="animate-spin" /> Rechnungs-Vorschau wird gerendert…
                  </div>
                )}
                {pdfError && (
                  <div className="px-4 py-6 text-amber-400 text-[12.5px]" data-testid={`${testIdPrefix}-pdf-error`}>
                    Vorschau nicht verfügbar: {pdfError}
                  </div>
                )}
                {pdfUrl && !pdfLoading && (
                  <iframe
                    src={pdfUrl}
                    title="Rechnungs-Vorschau"
                    className="w-full bg-white"
                    style={{ height: "520px" }}
                    data-testid={`${testIdPrefix}-pdf-iframe`}
                  />
                )}
              </div>
            </div>
          )}

          {bodyPreview && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">E-Mail-Vorschau</div>
              <div className="px-3 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 text-[12.5px] leading-relaxed max-h-64 overflow-y-auto whitespace-pre-line" data-testid={`${testIdPrefix}-preview`}>
                {bodyPreview}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-zinc-800 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={sending}
                  className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 rounded-lg text-[13px] font-bold disabled:opacity-40"
                  data-testid={`${testIdPrefix}-cancel`}>
            Abbrechen
          </button>
          <button onClick={confirm} disabled={sending || !recipient}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 rounded-lg text-[13px] font-bold flex items-center gap-1.5 disabled:opacity-40"
                  data-testid={`${testIdPrefix}-confirm`}>
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sende …" : "Jetzt senden"}
          </button>
        </div>
      </div>
    </div>
  );
}
